<i18n src="./resources/crm/contact-locales.json"></i18n>

<template>
  <v-dialog v-model="dialog" v-resize="onResize" width="480" max-width="480" @click:outside="cancelSelection">
    <v-card color="surface lighten-1">
      <v-card-title primary-title>
        {{ $t('dialogs.linkCrmToInteraction.title') }}

        <v-btn class="close-btn" x-small icon @click="cancelSelection">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-row align="center" class="py-2 px-6" no-gutters>
        <EcsSearchInput
          v-if="dialog"
          ref="searchInput"
          :placeholder="$t('dialogs.linkCrmToInteraction.searchPlaceholder')"
          @search-input-changed="handleInputChanged"
          @search-input-enter="handleBasicSearch"
        >
          <template v-slot:append-icon="">
            <v-menu v-model="advancedSearchMenu" :close-on-content-click="false" attach offset-y left>
              <template v-slot:activator="{ on }">
                <v-btn role="button" icon small v-on="on">
                  <v-icon>
                    mdi-chevron-down
                  </v-icon>
                </v-btn>
              </template>

              <v-card>
                <v-card-text>
                  <v-text-field ref="firstName" v-model="filters.firstName" dense label="First Name"></v-text-field>
                  <v-text-field ref="lastName" v-model="filters.lastName" dense label="Last Name"></v-text-field>
                  <v-text-field
                    ref="phoneNumber"
                    v-model="filters.phoneNumber"
                    dense
                    label="Phone Number"
                  ></v-text-field>
                  <v-text-field ref="email" v-model="filters.email" dense label="E-mail"></v-text-field>
                  <v-text-field ref="company" v-model="filters.company" dense label="Company"></v-text-field>
                  <v-text-field ref="customerId" v-model="filters.customerId" dense label="Customer ID"></v-text-field>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="closeAdvancedSearch">
                    Cancel
                  </v-btn>
                  <v-btn color="primary" text @click="handleAdvancedSearch">
                    Search
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </template>
        </EcsSearchInput>
      </v-row>

      <v-card-text class="pa-0 surface">
        <div v-if="isLoading" class="crm-contacts-info-container">
          <div class="crm-contacts-info">
            <div v-once>{{ $t('loading') }}</div>
            <div>
              <v-progress-linear color="light-blue" indeterminate rounded height="6"></v-progress-linear>
            </div>
          </div>
        </div>
        <div v-else-if="!contacts.length" class="crm-contacts-info-container">
          <div v-once class="crm-contacts-info">
            {{ $t('dialogs.linkCrmToInteraction.noContactsFound') }}
          </div>
        </div>
        <div v-else>
          <v-list class="pa-0">
            <v-virtual-scroll
              ref="agentsContainer"
              class="scroller custom-scrollbar"
              :items="contacts"
              :item-height="48"
              :bench="1"
              :min-height="384"
              :max-height="1200"
              :height="totalAvailableHeight"
            >
              <template v-slot="{ item }">
                <v-list-item class="px-6">
                  <div
                    class="mr-2"
                    @mouseenter.stop="openContactCard($event, item)"
                    @mouseleave.stop="closeContactCard"
                  >
                    <EcsAvatar :contact-id="item.id" :alt="item.name" :size="40" no-animation />
                  </div>

                  <v-list-item-content>
                    <v-list-item-title v-text="item.name"></v-list-item-title>
                  </v-list-item-content>

                  <v-list-item-action class="mt-0 mb-0">
                    <v-btn icon @click="selectItem(item)">
                      <v-icon color="primary">mdi-link-variant</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </template>
            </v-virtual-scroll>
          </v-list>
          <div v-if="totalPages > 1" class="text-center">
            <v-pagination
              v-model="currentPage"
              class="crm-contact-pagination"
              :dark="$vuetify.theme.dark"
              :length="totalPages"
              :total-visible="5"
            ></v-pagination>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="px-6">
        <v-spacer></v-spacer>
        <v-btn text @click="cancelSelection">
          <span v-once>{{ $t('dialogs.linkCrmToInteraction.buttons.cancel') }}</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import BusinessProcessService from '@/services/businessProcessService';
import ContactService from '@/services/contactService';
import EcsAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsAvatar';
import EcsCrmContact from '@/classes/contacts/EcsCrmContact';

export default {
  name: 'InteractionCrmContactSelectionDialog',

  components: { EcsAvatar },

  props: {
    businessProcessId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isLoading: true,
      contacts: [],
      totalAvailableContacts: 0,
      totalAvailableHeight: 0,
      dialog: false,
      advancedSearchMenu: false,
      filters: {
        firstName: null,
        lastName: null,
        phoneNumber: null,
        email: null,
        company: null,
        customerId: null
      },
      maxNumberOfResults: 24,
      currentPage: 1,
      crmTemplate: [
        {
          name: 'firstName',
          label: 'First name'
        },
        {
          name: 'lastName',
          label: 'Last name'
        },
        {
          name: 'email',
          label: 'E-mail'
        },
        {
          name: 'company',
          label: 'Company'
        }
      ]
    };
  },

  computed: {
    hasValidFilters() {
      return Object.values(this.filters).some(value => !!value);
    },

    totalPages() {
      return this.totalAvailableContacts === 0 ? 1 : Math.ceil(this.totalAvailableContacts / this.maxNumberOfResults);
    }
  },

  watch: {
    currentPage(page) {
      if (this.dialog) {
        this.handleSearch(page);
      }
    }
  },

  created() {
    this.calculateHeight();
    BusinessProcessService.getCrmTemplate(this.businessProcessId).then(response => {
      if (response.data && response.data.length) {
        this.crmTemplate = response.data
          .filter(item => item.visible)
          .sort((i1, i2) => i1.order - i2.order)
          .map(item => ({
            name: item.standardFieldName,
            label: item.customFieldName
          }));
      }
    });
  },

  methods: {
    handleInputChanged(input) {
      if (!input) {
        this.resetFilters();
        this.handleSearch(1);
      }
    },

    handleBasicSearch(input) {
      Object.keys(this.filters).forEach(key => {
        const regex = RegExp(`${key}:'([\\w\\s]+)'`);
        const value = regex.exec(input);
        this.filters[key] = value ? value[1] : null;
      });

      this.handleSearch(1);
    },

    handleAdvancedSearch() {
      this.$refs.searchInput.searchText = Object.entries(this.filters)
        .filter(([key, value]) => !!value)
        .map(([key, value]) => `${key}:'${value}'`)
        .join(' ');

      this.advancedSearchMenu = false;
      this.handleSearch(1);
    },

    closeAdvancedSearch() {
      this.advancedSearchMenu = false;
      this.resetFilters();
    },

    handleSearch(page) {
      let service;

      this.contacts = [];
      this.isLoading = true;

      const searchInput = this.$refs.searchInput ? this.$refs.searchInput.searchText : null;
      if (this.hasValidFilters || !searchInput) {
        service = ContactService.advancedSearchCrmContact(this.filters, this.maxNumberOfResults, page);
      } else {
        service = ContactService.basicSearchCrmContact(searchInput, this.maxNumberOfResults, page);
      }

      service
        .then(response => {
          this.contacts = response.data.contacts.map(contact => new EcsCrmContact(contact));
          this.totalAvailableContacts = response.data.totalNumberOfMatches;
          this.currentPage = page;
          this.calculateHeight();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    open() {
      this.handleSearch(1);
      this.dialog = true;
    },

    /**
     * It calculates the available height
     */
    calculateHeight() {
      const dialogTitleHeight = 54;
      const dialogSubtitle = 48;
      const searchSectionHeight = 53;
      const actionsHeight = 52;
      const paginationHeight = this.totalPages > 1 ? 28 : 0;

      this.totalAvailableHeight =
        window.innerHeight * 0.9 -
        (dialogTitleHeight + dialogSubtitle + searchSectionHeight + actionsHeight + paginationHeight);
    },

    cancelSelection() {
      this.close();
    },

    close() {
      this.$refs.searchInput.searchText = '';
      this.dialog = false;
      this.resetFilters();
      this.currentPage = 1;
    },

    onResize() {
      this.calculateHeight();
    },

    selectItem(contact) {
      this.$emit('contact-selected', contact);
      this.close();
    },

    resetFilters() {
      this.filters.firstName = null;
      this.filters.lastName = null;
      this.filters.phoneNumber = null;
      this.filters.email = null;
      this.filters.company = null;
      this.filters.customerId = null;
    },

    openContactCard(event, contact) {
      this.$root.$emit('open-crm-contact-card', {
        contact,
        crmTemplate: this.crmTemplate,
        x: event.target.getBoundingClientRect().left,
        y: event.target.getBoundingClientRect().bottom - 8
      });
    },

    closeContactCard() {
      this.$root.$emit('close-crm-contact-card');
    }
  }
};
</script>

<style lang="scss" scoped>
.close-btn {
  position: absolute;
  right: 24px;
}

.crm-contacts-info-container {
  padding: 16px;
  display: grid;
  height: calc(100vh * 0.9 - 207px);
}

.crm-contacts-info {
  text-align: center;
  align-self: center;
  justify-self: center;
  width: 75%;
}
</style>

<style scoped>
.crm-contact-pagination >>> .v-pagination {
  background-color: #ffffff;
}

.crm-contact-pagination >>> .v-pagination.theme--dark {
  background-color: #1e1e1e;
}

.crm-contact-pagination >>> .v-pagination__navigation {
  height: 20px;
  width: 20px;
  box-shadow: none;
  background: transparent;
  margin: 4px;
}

.crm-contact-pagination >>> .v-pagination__navigation > .v-icon {
  font-size: 12px;
}

.crm-contact-pagination >>> .v-pagination__navigation:focus {
  outline: none;
}

.crm-contact-pagination >>> .v-pagination__item {
  height: 20px;
  font-size: 12px;
  min-width: 20px;
  box-shadow: none;
  background: transparent;
  margin: 4px;
}

.crm-contact-pagination >>> .v-pagination__item:focus {
  outline: none;
}

.crm-contact-pagination >>> .v-pagination__more {
  width: 20px;
  height: 20px;
  margin: 4px;
  font-size: 12px;
}
</style>
